import { useState } from 'react'
import { Form, useActionData, useNavigation } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { Button } from '#app/components/ui/button.tsx'
import { Input } from '#app/components/ui/input.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import {
	QUOTE_REQUEST_INTENT,
	isQuoteRequestResult,
} from '#app/utils/quote-request.ts'

/**
 * The quote request.
 *
 * Fulfilment is manual to begin with: a request lands in the database, an
 * operator is emailed, and they pass it to businesses in that category. That
 * is a real service, so the form asks for the work rather than for a place on
 * a list.
 *
 * ## The promise is scoped to what we control
 *
 * We cannot guarantee a business replies, and we have never routed a lead, so
 * the copy claims neither. It states what *we* do — pass the details on — and
 * commits to chasing it if the reader hears nothing. That commitment is the
 * important part: it converts the failure mode from silent non-delivery, which
 * would be the first promise this site actively broke, into an outcome that is
 * still handled.
 *
 * A confirmation email goes out immediately for the same reason. A form that
 * swallows a submission and says nothing is indistinguishable from a broken
 * one.
 *
 * ## Two things it never says
 *
 * Never "we'll match you with the best pros". Matching is a curation claim,
 * and this site has spent considerable effort removing claims it cannot
 * evidence. It also never implies the ranking above was assembled to feed it.
 *
 * ## Placement
 *
 * Always below the ranking, never above. Above it, the ranking reads as bait
 * for a lead funnel and the reference-work positioning goes with it. The
 * reader gets the answer they came for, then the offer.
 *
 * ## Two shapes
 *
 * `section` runs full width under a ranking, where the surrounding page is
 * also full width and a boxed form would read as an ad unit — the one thing
 * the placement rule above exists to prevent.
 *
 * `card` sits in the listing sidebar, where every neighbour is reference
 * material and this is the only thing to *do*. Section styling there made it
 * read as more page. The card also drops the two-column field grid: the grid
 * breakpoint is viewport-width, so in a narrow column it would pair fields
 * side by side with nowhere to put them.
 */

export interface QuoteRequestFormProps {
	/** Category the request belongs to. Required — demand is counted by trade. */
	categoryId: string
	categoryName: string
	/** Set when the reader is on a city ranking. */
	cityId?: string | null
	cityName?: string | null
	/** Set when the reader started from a specific listing. */
	businessId?: string | null
	/**
	 * Set on a listing page. Changes who the request is *for*: a reader on
	 * Elevated Roofing's page wants a quote from Elevated Roofing, not from the
	 * category, and a generic form there would collect the wrong intent and
	 * mislead about where the request goes.
	 *
	 * It is also the more valuable record — named demand for one business is
	 * what makes a sales call to that owner worth taking.
	 */
	businessName?: string | null
	/** Rendered path, stored so a row joins to its pageview. */
	sourcePath: string
	/** How the form is framed. See the note above. Defaults to `section`. */
	variant?: 'section' | 'card'
}

const TIMELINES = [
	{ value: 'asap', label: 'As soon as possible' },
	{ value: 'weeks', label: 'Next few weeks' },
	{ value: 'months', label: 'Next few months' },
	{ value: 'planning', label: 'Just planning' },
] as const

export function QuoteRequestForm({
	categoryId,
	categoryName,
	cityId,
	cityName,
	businessId,
	businessName,
	sourcePath,
	variant = 'section',
}: QuoteRequestFormProps) {
	const card = variant === 'card'
	const shell = card ? 'bg-muted/30 rounded-lg border p-5' : 'border-t pt-8'
	const heading = card
		? 'mb-1.5 font-serif text-base tracking-tight'
		: 'mb-2 font-serif text-xl tracking-tight'
	const navigation = useNavigation()
	/*
	 * The result of the submission, rendered.
	 *
	 * The first version returned { ok, error } from the action and read none of
	 * it, so the fields stayed filled and nothing on the page confirmed or
	 * failed — the exact silent form this component's own comment argues
	 * against, relying on an email nobody can see arriving.
	 */
	const actionData = useActionData()
	/*
	 * Recognised by its discriminator, not by a cast.
	 *
	 * This renders on three routes, so it cannot type against a single
	 * `typeof action`. A bare cast would let a second intent added to any of
	 * them return a differently shaped object that this component displays as a
	 * quote result; the guard simply does not match it.
	 */
	const result = isQuoteRequestResult(actionData) ? actionData : undefined
	const [consented, setConsented] = useState(false)
	const submitting =
		navigation.state === 'submitting' &&
		navigation.formData?.get('intent') === QUOTE_REQUEST_INTENT

	const where = cityName ? `${cityName}` : 'Dallas–Fort Worth'
	const target = businessName ?? `${categoryName.toLowerCase()}s in ${where}`

	if (result?.ok) {
		return (
			<section className={shell}>
				<h2 className={heading}>Request sent</h2>
				<p className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
					We have your details and a confirmation is on its way to your inbox.
					We are passing the request to {target}. If you have not heard anything
					within two business days, reply to that email and we will chase it.
				</p>
			</section>
		)
	}

	return (
		<section className={shell}>
			<h2 className={heading}>
				{businessName
					? `Request a quote from ${businessName}`
					: `Get quotes from ${categoryName.toLowerCase()}s in ${where}`}
			</h2>
			{/*
			 * What the form does with the details is said once, on the consent
			 * checkbox, which is where it is load-bearing. Repeating it here as a
			 * preamble padded the top of a narrow sidebar with a sentence the
			 * reader can infer from the heading and the fields.
			 *
			 * The chase commitment survives the trim in every variant. It is the
			 * only part a reader cannot guess, and the only part that separates
			 * this from every other form that swallows a lead.
			 */}
			<p
				className={`text-muted-foreground max-w-2xl leading-relaxed ${
					card ? 'mb-4 text-xs' : 'mb-5 text-sm'
				}`}
			>
				{businessName ? (
					<>
						Not heard back within two business days? Tell us and we will chase
						it, and point you at other {categoryName.toLowerCase()}s we score
						well.
					</>
				) : (
					<>
						Tell us what you need and we will pass it to well-scoring{' '}
						{categoryName.toLowerCase()}s in {where} so they can contact you. If
						you have not heard from anyone within two business days, tell us and
						we will chase it. We do not choose a business for you — the ranking
						above is the recommendation.
					</>
				)}
			</p>

			<Form method="POST" className="max-w-2xl">
				{/* Same bot trap the feedback form uses. */}
				<HoneypotInputs />
				<input type="hidden" name="intent" value={QUOTE_REQUEST_INTENT} />
				<input type="hidden" name="categoryId" value={categoryId} />
				{cityId ? <input type="hidden" name="cityId" value={cityId} /> : null}
				{businessId ? (
					<input type="hidden" name="businessId" value={businessId} />
				) : null}
				<input type="hidden" name="sourcePath" value={sourcePath} />

				{/*
				 * The design-system controls, not hand-rolled ones.
				 *
				 * Every field here was a raw element carrying a copied class string
				 * that had drifted from `Input`: h-10 against h-9, ring-1 against
				 * ring-[3px], no shadow, no aria-invalid state, and `text-sm` — under
				 * 16px, which makes iOS Safari zoom the viewport the moment a thumb
				 * lands in a field. `Input` sets `text-base md:text-sm` precisely to
				 * avoid that, and copying its look by hand lost the one class that
				 * was not cosmetic.
				 */}
				<div className={`grid gap-3 ${card ? '' : 'sm:grid-cols-2'}`}>
					<label className="block">
						<span className="mb-1 block text-sm font-medium">Name</span>
						<Input name="name" required autoComplete="name" />
					</label>
					<label className="block">
						<span className="mb-1 block text-sm font-medium">Email</span>
						<Input name="email" type="email" required autoComplete="email" />
					</label>
					<label className="block">
						<span className="mb-1 block text-sm font-medium">
							Phone <span className="text-muted-foreground">(optional)</span>
						</span>
						<Input name="phone" type="tel" autoComplete="tel" />
					</label>
					<label className="block">
						<span className="mb-1 block text-sm font-medium">Timeline</span>
						{/*
						 * The one native <select> left in the app, which is why its
						 * indicator was the operating system's arrow rather than the
						 * chevron every other control uses.
						 *
						 * Radix renders a hidden native select from `name`, so the value
						 * still posts with the rest of the form. Without JS the trigger
						 * cannot be opened and that hidden field submits the default —
						 * acceptable only because `timeline` is optional server-side and
						 * "next few weeks" is a reasonable thing to assume. A required
						 * field could not degrade this way.
						 */}
						<Select name="timeline" defaultValue="weeks">
							<SelectTrigger className="h-9 w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{TIMELINES.map((t) => (
									<SelectItem key={t.value} value={t.value}>
										{t.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</label>
				</div>

				<label className="mt-3 block">
					<span className="mb-1 block text-sm font-medium">
						What do you need done?{' '}
						<span className="text-muted-foreground">(optional)</span>
					</span>
					<Textarea name="detail" rows={3} className="min-h-0" />
				</label>

				{/*
				 * Consent is separate, unchecked by default, and says exactly what it
				 * permits. The privacy policy allows sharing personal details only
				 * with consent, so this checkbox is the mechanism that makes any
				 * future hand-off to a business lawful — it cannot be implied by
				 * submitting the form.
				 *
				 * Set one step below body text, not smaller. Shrinking the consent
				 * line specifically is the recognisable pattern for burying it, and
				 * a site that spends this much effort not overclaiming cannot be
				 * caught doing the one UI move that reads as hiding terms.
				 *
				 * It said "contact you about this job" until a category that is not
				 * a job — a dentist, an accountant — made it obvious that the copy
				 * had quietly assumed the trades pilot was the whole directory.
				 */}
				<label className="mt-4 flex items-start gap-2.5">
					<input
						type="checkbox"
						name="consentedToShare"
						checked={consented}
						onChange={(e) => setConsented(e.target.checked)}
						required
						className="mt-0.5 size-4 shrink-0"
					/>
					<span className="text-muted-foreground text-xs leading-relaxed">
						You agree we can pass these details to{' '}
						{businessName ?? `${categoryName.toLowerCase()}s in ${where}`} so
						they can get in touch. We do not sell your details to anyone else,
						and you can ask us to delete them at any time.
					</span>
				</label>

				{result?.ok === false ? (
					<p
						role="alert"
						className="border-destructive/40 text-destructive mt-4 border-l-2 pl-3 text-sm"
					>
						{result.error}
					</p>
				) : null}

				<Button
					className={card ? 'mt-4 w-full' : 'mt-4'}
					disabled={submitting || !consented}
					type="submit"
				>
					{submitting
						? 'Sending…'
						: businessName
							? 'Request a quote'
							: 'Request quotes'}
				</Button>
			</Form>
		</section>
	)
}
